import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { alpha } from "@mui/material/styles"

import NewFeatureChip from "../../../common/components/NewFeatureChip"
import Row from "../../../common/components/Row"

const SUPPORT_EMAIL = "support@onebasecrm.com"

export function ConciergeTabLabel() {
  return (
    <Row sx={{ alignItems: "center", gap: 0.5 }}>
      Concierge
      <NewFeatureChip label="New" size="small" />
    </Row>
  )
}

/**
 * mailto: URL for customers to express Concierge interest to support.
 */
export function buildConciergeInterestMailto(profile) {
  const accountName =
    profile?.name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    "a OneBase customer"
  const accountEmail = profile?.email || ""
  const subject = "Interested in SmartSetter Concierge"
  const body = [
    "Hi OneBase support,",
    "",
    "I'm interested in the SmartSetter Concierge feature for my account.",
    "",
    `Account name: ${accountName}`,
    accountEmail ? `Account email: ${accountEmail}` : null,
    "",
    "Please let me know next steps to get a Concierge assigned.",
    "",
    "Thanks,",
    accountName,
  ]
    .filter((line) => line !== null)
    .join("\n")

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`
}

export function ConciergeHero({ connectedCount = 0 }) {
  const hasConcierges = connectedCount > 0
  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "16px",
        px: { xs: 2.5, sm: 3 },
        py: { xs: 2.5, sm: 3 },
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        backgroundImage: (theme) =>
          `linear-gradient(135deg, ${alpha(
            theme.palette.secondary.main,
            0.04
          )} 0%, ${alpha(theme.palette.common.white, 0)} 55%)`,
        boxShadow: "0 10px 24px -18px rgba(15, 23, 42, 0.22)",
      }}
    >
      <Row
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 1.25,
        }}
      >
        <Row sx={{ alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              backgroundColor: (theme) =>
                alpha(theme.palette.secondary.main, 0.1),
              color: "secondary.main",
            }}
          >
            <SupportAgentOutlinedIcon fontSize="small" />
          </Box>
          <NewFeatureChip label="White-glove support" size="small" />
        </Row>
        {hasConcierges && (
          <Box
            sx={{
              minWidth: 88,
              px: 1.75,
              py: 1.25,
              borderRadius: "12px",
              textAlign: "center",
              backgroundColor: (theme) =>
                alpha(theme.palette.secondary.main, 0.06),
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.secondary.main, 0.14),
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, lineHeight: 1.1, color: "text.primary" }}
            >
              {connectedCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              connected
            </Typography>
          </Box>
        )}
      </Row>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          letterSpacing: "-0.01em",
          mb: 0.75,
          color: "text.primary",
        }}
      >
        SmartSetter Concierge
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ lineHeight: 1.65, width: "100%" }}
      >
        {hasConcierges
          ? "Your assigned Concierges can work in your OneBase account on your behalf. Actions stay attributed to them in the activity timeline."
          : "Get a dedicated SmartSetter Concierge on your account — they help with CRM work while you stay in control."}
      </Typography>
    </Box>
  )
}
